<template class="PaymentsToVCFS">
    <div v-if="isLoading" class="content-container">
        <div class="loading">
            <span class="loader"></span>
        </div>
    </div>

    <div v-else class="content-container">
        <div class="top-content">
            <div class="flex-row-centered center-buttons">
                <button @click="proceedToVcfsCreationPopUp" class="button-styling"> Create Payment</button>
                <button @click="proceedToVcfsConfirmationPopUp" class="button-styling">Confirm Payment</button>
            </div>
        </div>
        <div class="table-container">
            <TablePersistentChecking
            ref="table"
            :Rows="vcfsPaymentsList"
            :Cols="cols"
            :Labels="labels"
            :HasCheckBox="true"
            :SingleSelection="true"
            :RowCheckableFilter="x=>x.paymentDate=='' || x.paymentDate==null "
            :DefaultSortCols="defaultSortCols"
            :DefaultSortOrders="defaultSortOrders"
            >
            <template #amount="props">
                {{ MonetaryValueWithCommas(props.value.amount) }}
            </template>
            <template #amount-1="props">
                <div v-if="isDownloadDisabled(props.value.paymentReference) == true">
                    <button class="table-button" disabled>
                        Export Report
                    </button>
                </div>
                <div v-else>
                    <button @click="downloadFile(props.value.paymentReference)" class="table-button">
                        Export Report
                    </button>
                </div>
            </template>
            </TablePersistentChecking>            
        </div>
        <VcfsConfirmPaymentsPopUp
            v-model:modelValue="checkedRow"
            @confirm="refreshAfterConfirm"
            @close="ClearTableSelection()"
        />
        <VcfsCreatePaymentsPopUp
            v-model:modelValue="createPaymentPopup"
            @confirm="refreshAfterConfirm"
        />
    </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from "vue";
import TablePersistentChecking, {
    ITableExpose,
} from "@/components/TablePersistentChecking.vue";
import { downloadManager, vcfsPaymentsService } from "@/services/network";
import useNotifier from "@/services/composables/Notifier";
import { IVcfsPaymentItem } from "@/Interfaces/Payments/IVcfsPaymentItem";
import VcfsCreatePaymentsPopUp from "@/components/VcfsCreatePaymentsPopUp.vue";
import useNotifierWithErrFormatter from "@/services/composables/NotifierWithErrFormatter";
import VcfsConfirmPaymentsPopUp from "@/components/VcfsConfirmPaymentsPopUp.vue";
import { MonetaryValueWithCommas } from "@/services/helpers/Helper";
import { authService } from "@/auth/authSingleton";
import { FileDownloadType } from "@/models/enums/FileDownloadTypes";

const cols: (keyof IVcfsPaymentItem)[] = [
    "paymentReference",
    "paymentFrom",
    "paymentTo",
    "paymentDate",
    "cutoffDate",
    "amount",
    "amount"
];
const labels = [
    "Payment Reference",
    "Payment From",
    "Payment To",
    "Payment Date",
    "Cutoff Date",
    "Amount",
    "Report"
];
interface DownloadStatusHelperInterface {
    paymentReference: string;
    downloadPressed: boolean;
}
const sortCol: keyof IVcfsPaymentItem = 'paymentReference';
const defaultSortCols = ref([cols.findIndex((col) => {return col === sortCol;}),]);
const defaultSortOrders = ref<(1|-1)[]>([-1]);
const table = ref<ITableExpose<IVcfsPaymentItem[]>>();
const checkedRow = ref<IVcfsPaymentItem>();
const createPaymentPopup = ref(false);
const vcfsPaymentsList = ref<IVcfsPaymentItem[]>([]);
const downloadStateHelperList = ref<DownloadStatusHelperInterface[]>([]);
const emailAddress = ref();

const isLoading = ref(false);  

onMounted(async () => {
    getVcfsPayments();
    emailAddress.value = authService.getEmailAddress();
});
async function getVcfsPayments() {
    try {
        isLoading.value = true;
        vcfsPaymentsList.value = (await vcfsPaymentsService.get()).data;
        updateSelection();
        createDownloadStatusHelperElement()
    } catch (error) {
        useNotifierWithErrFormatter().error({errorType: "Getting VCFS payments", error: error});
    } finally {
        isLoading.value = false;
    }
}
function proceedToVcfsCreationPopUp(){
    createPaymentPopup.value = true
}
function proceedToVcfsConfirmationPopUp() {
    const selectedRow = table.value?.GetChecked()?.[0]  as IVcfsPaymentItem | undefined;
    if (selectedRow == undefined)  {
        useNotifier().notify({msg:'No item has been checkmarked. Please select the payment that you wish to confirm.'});
        return;
    }
    checkedRow.value = selectedRow
}
const updateSelection = () => {
    checkedRow.value = undefined;
};
function refreshAfterConfirm() {
    getVcfsPayments();
    updateSelection();
    createPaymentPopup.value = false;
    ClearTableSelection()
}
function ClearTableSelection(){
    table.value?.ClearChecked()
}
function createDownloadStatusHelperElement() {
    downloadStateHelperList.value = vcfsPaymentsList.value.map((x) => ({
        paymentReference: x.paymentReference,
        downloadPressed: false,
    }));
}
function isDownloadDisabled(paymentReference: string) {
    const helperReference = downloadStateHelperList.value.find(
        (x) => x.paymentReference === paymentReference
    );
    return helperReference?.downloadPressed;
}
async function downloadFile(paymentReference: string) {  
    try {
        await downloadManager.post({
            userName: emailAddress.value,
            type: FileDownloadType.VCFS_PAYMENT_REPORT,
            requestData:
            {
                PaymentReference: paymentReference
            }
        })
        useNotifierWithErrFormatter().success({
            msg: "Vcfs payment report was queued for download"
        });
        const i = downloadStateHelperList.value.findIndex(x => x.paymentReference === paymentReference);    
        downloadStateHelperList.value[i] = {
            ...downloadStateHelperList.value[i],
            downloadPressed: true,
        };
    } catch(error) {
        useNotifierWithErrFormatter().error({
            errorType: "Vcfs payment report download queue error",
            error,
        });
    }

}
</script>
<style scoped lang="scss">
@import "@/assets/styles/layout.scss";
@import "@/assets/styles/buttons.scss";
@import "@/assets/styles/table.scss";
.button-styling {
    margin-bottom: 0px
}
:deep(.table-button){
    display: flex;
    margin: auto;
}
</style>